import React, { useState, useEffect, useMemo } from 'react';
import DataTable from './components/DataTable/DataTable';
import { DUPLICATE_CONFLICTS_COLUMNS, FIELD_CONFLICTS_COLUMNS } from './constants/tableColumns';
import API_BASE_URL from './config';
import './JobConflicts.css';

/**
 * JobConflictsTableView - Refactored Job Conflicts using DataTable component
 * 
 * Sprint 20: Replaces custom table code with DataTable
 * Handles two types of conflicts:
 * 1. Duplicate Job ID conflicts (different columns, no bulk actions)
 * 2. Field Change conflicts (side-by-side comparison, bulk actions)
 */

export default function JobConflictsTableView({ currentUser }) {
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState([]);
  const [rowSelection, setRowSelection] = useState({});

  useEffect(() => {
    fetchConflicts();
  }, []);

  const fetchConflicts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/job-conflicts`, {
        credentials: 'include'
      });
      const data = await response.json();
      setConflicts(data.conflicts || []);
    } catch (error) {
      console.error('Error fetching conflicts:', error);
      setConflicts([]);
    } finally {
      setLoading(false);
    }
  };

  const resolveConflict = async (conflictId, choice, customValue = null) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/job-conflicts/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ conflict_id: conflictId, choice, custom_value: customValue })
      });
      
      if (response.ok) {
        setConflicts(conflicts.filter(c => c.id !== conflictId));
        alert('✅ Conflict resolved successfully!');
      } else {
        alert('❌ Failed to resolve conflict');
      }
    } catch (error) {
      console.error('Error resolving conflict:', error);
      alert('❌ Error resolving conflict');
    }
  };

  const batchResolve = async (action) => {
    if (selectedIds.length === 0) {
      alert('Please select conflicts to resolve');
      return;
    }

    const actionLabel = action === 'use_new_all' ? 'use new values' : 'keep current values';
    if (!window.confirm(`Are you sure you want to ${actionLabel} for ${selectedIds.length} conflict(s)?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/job-conflicts/resolve-batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action, conflict_ids: selectedIds })
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`✅ Resolved ${data.resolved_count} conflicts`);
        fetchConflicts();
        setSelectedIds([]);
        setRowSelection({});
      } else {
        alert('❌ Failed to resolve conflicts');
      }
    } catch (error) {
      console.error('Error batch resolving:', error);
      alert('❌ Error resolving conflicts');
    }
  };

  // Split conflicts by type
  const duplicateConflicts = useMemo(() => 
    conflicts.filter(c => c.conflict_type === 'duplicate_job_id'),
    [conflicts]
  );

  const fieldConflicts = useMemo(() => 
    conflicts.filter(c => c.conflict_type !== 'duplicate_job_id'),
    [conflicts]
  );

  // Apply filter
  const filteredConflicts = useMemo(() => {
    if (filter === 'all') return conflicts;
    if (filter === 'duplicate_job_id') return duplicateConflicts;
    return conflicts.filter(c => c.field_name === filter);
  }, [conflicts, filter, duplicateConflicts]);

  const filteredDuplicates = useMemo(() => 
    filteredConflicts.filter(c => c.conflict_type === 'duplicate_job_id'),
    [filteredConflicts]
  );

  const filteredFields = useMemo(() => 
    filteredConflicts.filter(c => c.conflict_type !== 'duplicate_job_id'),
    [filteredConflicts]
  );

  // Track selected rows for bulk actions
  useEffect(() => {
    const selected = Object.keys(rowSelection)
      .filter(key => rowSelection[key])
      .map(key => filteredFields[parseInt(key)]?.id)
      .filter(Boolean);
    setSelectedIds(selected);
  }, [rowSelection, filteredFields]);

  // Add action buttons to field conflicts columns
  const fieldConflictsColumnsWithActions = useMemo(() => [
    ...FIELD_CONFLICTS_COLUMNS,
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="d-flex gap-1 flex-column">
          <button 
            className="btn btn-sm btn-warning px-2 py-1"
            onClick={(e) => {
              e.stopPropagation();
              resolveConflict(row.original.id, 'keep_current');
            }}
            title="Keep the current value in the database"
            style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
          >
            Keep
          </button>
          <button 
            className="btn btn-sm btn-info px-2 py-1"
            onClick={(e) => {
              e.stopPropagation();
              resolveConflict(row.original.id, 'use_new');
            }}
            title="Use the newly scraped value"
            style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
          >
            Use New
          </button>
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
      size: 80,
    },
  ], [conflicts]);

  // Add action button to duplicate conflicts columns
  const duplicateConflictsColumnsWithActions = useMemo(() => [
    ...DUPLICATE_CONFLICTS_COLUMNS,
    {
      id: 'actions',
      header: 'Action',
      cell: ({ row }) => (
        <button 
          className="btn btn-sm btn-primary"
          onClick={(e) => {
            e.stopPropagation();
            resolveConflict(row.original.id, 'keep_production');
          }}
          title="Keep production job ID, skip dev version on future syncs"
        >
          Keep Production
        </button>
      ),
      enableSorting: false,
      enableHiding: false,
      size: 150,
    },
  ], [conflicts]);

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading conflicts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="job-conflicts-container" style={{ padding: '20px', maxWidth: '95vw', margin: '0 auto' }}>
      <h1>Review Data Conflicts ({conflicts.length})</h1>
      
      <div className="conflict-description alert alert-info">
        <p className="mb-0">
          When the scraper finds data that differs from what's in the database, it creates a conflict for your review. 
          Choose whether to keep the current value or use the newly scraped value.
        </p>
      </div>
      
      <div className="conflict-toolbar mb-4">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
          {/* Filter Buttons */}
          <div className="btn-group" role="group">
            <button 
              onClick={() => setFilter('all')} 
              className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
            >
              All ({conflicts.length})
            </button>
            <button 
              onClick={() => setFilter('title')} 
              className={`btn ${filter === 'title' ? 'btn-primary' : 'btn-outline-primary'}`}
            >
              Title ({conflicts.filter(c => c.field_name === 'title').length})
            </button>
            <button 
              onClick={() => setFilter('due_date')} 
              className={`btn ${filter === 'due_date' ? 'btn-primary' : 'btn-outline-primary'}`}
            >
              Due Date ({conflicts.filter(c => c.field_name === 'due_date').length})
            </button>
            <button 
              onClick={() => setFilter('duplicate_job_id')} 
              className={`btn ${filter === 'duplicate_job_id' ? 'btn-primary' : 'btn-outline-primary'}`}
            >
              Duplicates ({duplicateConflicts.length})
            </button>
          </div>
          
          {/* Batch Actions */}
          <div className="d-flex gap-2">
            <button 
              onClick={() => batchResolve('use_new_all')} 
              disabled={selectedIds.length === 0}
              className="btn btn-info"
            >
              Use New for Selected ({selectedIds.length})
            </button>
            <button 
              onClick={() => batchResolve('keep_current_all')} 
              disabled={selectedIds.length === 0}
              className="btn btn-warning"
            >
              Keep Current for Selected ({selectedIds.length})
            </button>
          </div>
        </div>
      </div>

      {filteredConflicts.length === 0 ? (
        <div className="alert alert-success text-center">
          <h3>✅ No conflicts to review!</h3>
        </div>
      ) : (
        <>
          {/* Duplicate Job ID Conflicts Section */}
          {filteredDuplicates.length > 0 && (
            <div className="duplicate-conflicts-section mb-5">
              <h2>⚠️ Duplicate Job ID Conflicts</h2>
              <p className="text-muted mb-3">
                These jobs exist in both dev and production with different job IDs but same Organization + RFP Number.
                Choose "Keep Production" to maintain the production job and skip the dev version on future syncs.
              </p>
              <DataTable
                columns={duplicateConflictsColumnsWithActions}
                data={filteredDuplicates}
                tableId="duplicate-conflicts-table"
                enableRowSelection={false}
                enableColumnResizing={true}
                enableColumnOrdering={true}
                enableFilters={false}
                enablePagination={false}
                className="duplicate-conflicts-table"
              />
            </div>
          )}

          {/* Field Change Conflicts Section */}
          {filteredFields.length > 0 && (
            <div className="field-conflicts-section">
              {filteredDuplicates.length > 0 && (
                <h2 className="mt-4">📝 Field Change Conflicts</h2>
              )}
              <DataTable
                columns={fieldConflictsColumnsWithActions}
                data={filteredFields}
                tableId="field-conflicts-table"
                enableRowSelection={true}
                rowSelection={rowSelection}
                onRowSelectionChange={setRowSelection}
                enableColumnResizing={true}
                enableColumnOrdering={true}
                enableFilters={false}
                enablePagination={false}
                className="field-conflicts-table"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

